import axios from 'axios';
import type { BaseLegal } from '@eleg360/types';

/** Presença de um contato numa visita, com a ficha da pessoa junto. */
export interface ParticipanteVisita {
  id: string;
  visita_id: string;
  contato_id: string;
  contato_nome: string;
  contato_bairro: string | null;
  contato_perfil: string[];
  contato_base_legal: BaseLegal;
  created_at: string;
}

/** Uma visita como aparece em "Visitas deste contato", na ficha. */
export interface VisitaDoContato {
  id: string;
  bairro: string | null;
  municipio: string | null;
  data_visita: string;
  status: Visita['status'];
  relatorio_campo: string | null;
  proximos_passos: string | null;
}

export interface Visita {
  id: string;
  tenant_id: string;
  assessor_id: string;
  lideranca_id?: string;
  municipio: string;
  bairro: string;
  data_visita: string;
  status: 'planejada' | 'concluida' | 'cancelada';
  /** "Resultado (o que foi tratado)" do protótipo. */
  relatorio_campo?: string;
  proximos_passos?: string;
  latitude?: number;
  longitude?: number;
  checkin_at?: string;
  created_at: string;
  updated_at: string;
}

const API_URL = import.meta.env.VITE_API_URL || '/api';

export const visitasApi = {
  create: async (
    data: {
      assessor_id: string;
      lideranca_id?: string;
      municipio: string;
      bairro: string;
      data_visita: string;
    },
    token: string,
  ) => {
    const res = await axios.post<Visita>(`${API_URL}/gab/agenda-interna`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  },

  list: async (token: string) => {
    const res = await axios.get<Visita[]>(`${API_URL}/gab/agenda-interna`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  },

  get: async (id: string, token: string) => {
    const res = await axios.get<Visita>(`${API_URL}/gab/agenda-interna/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  },

  update: async (id: string, data: Partial<Visita>, token: string) => {
    const res = await axios.put<Visita>(`${API_URL}/gab/agenda-interna/${id}`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  },

  doCheckin: async (
    id: string,
    data: { latitude: number; longitude: number; relatorio_campo?: string },
    token: string,
  ) => {
    const res = await axios.post<Visita>(`${API_URL}/gab/agenda-interna/${id}/checkin`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  },

  delete: async (id: string, token: string) => {
    await axios.delete(`${API_URL}/gab/agenda-interna/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },

  listarParticipantes: async (visitaId: string, token: string) => {
    const res = await axios.get<ParticipanteVisita[]>(
      `${API_URL}/gab/agenda-interna/${visitaId}/participantes`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return res.data;
  },

  /**
   * Em lote, e não um POST por pessoa: "+ rede de <liderança>" adiciona vários
   * de uma vez, e uma chamada por contato deixaria metade da rede registrada
   * quando a quarta falhasse. O servidor ignora quem já está na lista.
   */
  adicionarParticipantes: async (visitaId: string, contatoIds: string[], token: string) => {
    const res = await axios.post<ParticipanteVisita[]>(
      `${API_URL}/gab/agenda-interna/${visitaId}/participantes`,
      { contato_ids: contatoIds },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return res.data;
  },

  removerParticipante: async (visitaId: string, contatoId: string, token: string) => {
    await axios.delete(
      `${API_URL}/gab/agenda-interna/${visitaId}/participantes/${contatoId}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
  },

  /**
   * O "Salvar" do protótipo: resultado, próximos passos e situação numa
   * requisição só. Três PUTs em sequência deixariam a visita concluída sem
   * resultado quando o segundo falhasse.
   */
  concluir: async (
    id: string,
    dados: { relatorio_campo?: string; proximos_passos?: string },
    token: string,
  ) => {
    const res = await axios.post<Visita>(
      `${API_URL}/gab/agenda-interna/${id}/concluir`,
      dados,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return res.data;
  },

  /** "Visitas deste contato" — a pergunta inversa da lista de presença. */
  listarPorContato: async (contatoId: string, token: string) => {
    const res = await axios.get<VisitaDoContato[]>(
      `${API_URL}/gab/agenda-interna/contato/${contatoId}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return res.data;
  },
};
